// ---------------------------------------------------------------------------
// Nieve1 snow model: classic temperature-index (degree-day), the same
// partitioning and melt formulation HBV uses for snow — confirmed by
// decompiling EvalHid's real calculation engine (Evalhid.dll, class
// Nieve1.calculo_nieve1, ilspycmd — same method used for the other 7
// models), matching Annex B.1 of EvalHid's Technical Manual v1.1 (UPV) too,
// with the same TT/DD/No names as baseTemplate.ts.
// ---------------------------------------------------------------------------

export interface Snow1Parameters {
  /** TT (°C): liquid/solid precipitation threshold temperature. */
  tt: number;
  /** DD (mm/°C·step): degree-day melt factor. */
  dd: number;
}

interface Snow1State {
  /** No (mm): initial snow content. */
  no: number;
}

interface Snow1Detail {
  [key: string]: Float64Array;
  /** No (mm): snow content after the step. */
  snow: Float64Array;
  /** Snow melt for the step (mm), 0 if there's no melt. */
  melt: Float64Array;
}

interface Snow1Result {
  /** Resulting liquid precipitation (rain + melt), mm per step — the input the rainfall-runoff model receives. */
  liquidPrecipitation: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: Snow1Detail;
}

/**
 * Simulates the Nieve1 (degree-day) snow model for a single sub-basin.
 *
 * Melt is bounded to the available snow content (`Math.min`) so the stock
 * never goes negative — the Technical Manual doesn't spell this out, but
 * the decompiled DLL does have this exact clamp (`if (esc_nieve > no)
 * esc_nieve = no;`), confirmed line-for-line against
 * Nieve1.calculo_nieve1 — this is the real model's own behavior, not an
 * addition made for this port.
 */
export function calculateSnow1(
  precipitation: Float64Array,
  temperature: Float64Array,
  parameters: Snow1Parameters,
  initialState: Snow1State
): Snow1Result {
  const { tt, dd } = parameters;
  const n = precipitation.length;
  const liquidPrecipitation = new Float64Array(n);
  const detail: Snow1Detail = {
    snow: new Float64Array(n),
    melt: new Float64Array(n),
  };

  let snow = initialState.no;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    // With no temperature data, it's treated as "at the threshold" (no melt, precipitation stays as snow).
    const temp = Number.isFinite(temperature[t]) ? temperature[t] : tt;

    let melt = 0;
    if (temp > tt) {
      melt = Math.min(dd * (temp - tt), snow);
      snow -= melt;
      liquidPrecipitation[t] = p + melt;
    } else {
      snow += p;
      liquidPrecipitation[t] = 0;
    }

    detail.snow[t] = snow;
    detail.melt[t] = melt;
  }

  return { liquidPrecipitation, detail };
}
